<script setup lang="ts">
import { UIButton, UIButtonLink } from '@groover-dev/groover-ui'

import LanguageSwitcherV2 from '~/components/ui/LanguageSwitcherV2.vue'

import { useIsOnCmsLandingPage } from '~/composables/useIsOnCMSLandingPage'

import { useBandSignupStore } from '~/stores/bandSignup'
import { useLoginStore } from '~/stores/login'
import { useUserStore } from '~/stores/user'

import softScrollToDomId from '~/helpers/shared/softScrollToDomId'

type Emits = {
  close: []
}

const emit = defineEmits<Emits>()

const { IS_LOGGED_IN } = storeToRefs(useUserStore())
const { SET_DISPLAY: LOGIN_SET_DISPLAY } = useLoginStore()
const { SET_DISPLAY: BAND_SIGNUP_SET_DISPLAY } = useBandSignupStore()

const blogLink = 'https://blog.groover.co'
const route = useRoute()
const { isOnCmsLandingPage } = useIsOnCmsLandingPage()

const commonButtonProps = {
  theme: 'dark' as const,
  size: 'small' as const,
  color: 'neutral' as const,
  class: 'tw-whitespace-nowrap',
}

const isIndex = computed<boolean>(() => {
  return !!(
    route.name?.toString().startsWith('index___') && !IS_LOGGED_IN.value
  )
})

function scrollToDomId(id: string): void {
  softScrollToDomId(id)
}

function handleScroll(domId: string): void {
  scrollToDomId(domId)
}

function handleSigninClick(): void {
  LOGIN_SET_DISPLAY(true)
  emit('close')
}

function handleSignupClick(): void {
  BAND_SIGNUP_SET_DISPLAY(true)
  emit('close')
}
</script>

<template>
  <div class="mainGrid" :class="{ canDisplayRightHandSide: isIndex }">
    <div v-if="isIndex" class="split">
      <UIButtonLink
        v-bind="commonButtonProps"
        to="#"
        @click="handleScroll('mediasAndPros')"
      >
        {{ $t('nav.mobile.loggedOut.ourMediasAndPros') }}
      </UIButtonLink>
      <UIButtonLink
        v-bind="commonButtonProps"
        to="#"
        @click="handleScroll('howitworks')"
      >
        {{ $t('nav.mobile.loggedOut.howItWorks') }}
      </UIButtonLink> 
      <UIButtonLink
        v-bind="commonButtonProps"
        to="#"
        @click="handleScroll('landingPricing')"
      >
        {{ $t('nav.mobile.loggedOut.pricing') }}
      </UIButtonLink>
      <UIButtonLink
        v-bind="commonButtonProps"
        :to="blogLink"
        is-external
        target="_blank"
      >
        {{ $t('nav.mobile.loggedOut.blog') }}
      </UIButtonLink>
    </div>
    <div class="split" :class="{ 'tw-ml-auto': !isIndex }">
      <UIButtonLink
        v-if="!isOnCmsLandingPage"
        v-bind="commonButtonProps"
        data-test-id="curatorSignupLink"
        to="/influencer/signup/"
      >
        {{ $t('nav.mobile.loggedOut.becomeAPro') }}
      </UIButtonLink>
      <UIButtonLink
        data-test-id="loginLink"
        v-bind="commonButtonProps"
        class="tw-text-link-inverse"
        to="#"
        @click.prevent="handleSigninClick"
      >
        {{ $t('common.signIn') }}
      </UIButtonLink>
      <UIButton
        data-test-id="signupInitCTA"
        size="small"
        :text="$t('common.signUp')"
        @click="handleSignupClick"
      />
      <LanguageSwitcherV2
        v-if="!isOnCmsLandingPage"
        dropdown-position="bottom"
        locale-verbosity="short"
        theme="dark"
        no-redirect
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.mainGrid {
  @apply tw-grid tw-grid-cols-2 tw-gap-[45px];

  &:not(.canDisplayRightHandSide) {
    @apply tw-grid-cols-1;
  }

  @screen xl {
    @apply tw-flex tw-w-full tw-justify-between;
  }
}

.split {
  @apply tw-grid tw-items-center tw-gap-4 tw-text-center;

  grid-auto-flow: column;

  * {
    @apply tw-cursor-pointer;
  }
}
</style>
